<template>
  <div>
    <!-- Loading skeleton - only show when loading -->
    <Skeleton class="w-full!" height="20rem" v-if="loading" />
    <!-- Data table component - only show when not loading -->
    <DataTable
      v-else
      :value="allItems"
      :first="first"
      :loading="loading"
      rowHover
      :paginator="paginator"
      :rows="rows"
      v-model:selection="selectRows"
      :stripedRows="alternateRows"
      :scrollable="scrollable"
      :totalRecords="totalRecords"
      :lazy="lazy"
      paginatorTemplate="CurrentPageReport PrevPageLink NextPageLink"
      currentPageReportTemplate="Página {currentPage} de {totalPages}"
      :tableStyle="tableStyle"
      class="rounded-lg overflow-hidden border-border-default! border!"
      :class="tableClass"
      dataKey="id"
      :selectableRowDisabled="true"
      @page="onPageChange"
    >
      <!-- Checkbox column for row selection - appears at the beginning -->
      <Column
        v-if="showCheckbox"
        selectionMode="multiple"
        style="width: 3.5rem"
        headerStyle="width: 3.5rem"
      />

      <!-- Dynamic columns -->
      <Column
        v-for="col of columns"
        :key="col.field"
        :field="col.field"
        :header="!col.alignHeader && !slots[`${col.field}Header`] ? col.header : undefined"
        :sortable="col.sortable"
        :style="col.style || (['selected', 'select', 'checkbox'].includes(col.field) ? 'width: 3.5rem; text-align: center;' : undefined)"
        :headerClass="headerClass"
      >
        <template v-if="col.alignHeader || slots[`${col.field}Header`]" #header>
          <div class="flex w-full" :class="getJustifyClass(col.alignHeader)">
            <slot :name="`${col.field}Header`">
              {{ col.header }}
            </slot>
          </div>
        </template>
        <template #body="{ data, index }">
          <!-- Filas editables al inicio -->
          <div v-if="data.isEditable">
            <!-- Columna Estado especial para la primera fila editable -->
            <div
              v-if="col.field === 'status' && index === 0"
              class="flex items-center gap-2"
            >
              <button
                @click="addEditableRow"
                class="bg-blue-500 hover:bg-blue-600 text-white w-6 h-6 rounded text-xs font-bold transition-colors flex items-center justify-center"
                title="Agregar fila"
              >
                +
              </button>
              <button
                class="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                @click="assignRow(data)"
              >
                Asignar
              </button>
            </div>
            <!-- Columna Estado para otras filas editables -->
            <div v-else-if="col.field === 'status' && index > 0">
              <button
                @click="removeEditableRow(index)"
                class="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1"
              >
                🗑 Eliminar
              </button>
            </div>
            <!-- Otros campos editables -->
            <div v-else>
              <slot :name="`${col.field}Editable`" :data="data" :index="index">
                <CustomInput
                  v-model="data[col.field]"
                  class="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  :placeholder="col.header"
                />
              </slot>
            </div>
          </div>
          <!-- Filas normales -->
          <div v-else>
            <slot :name="col.field" :data="data">
              <span
                :class="`${rowClass} ${
                  col.alignRow ? `block w-[97%] ${getTextAlignClass(col.alignRow)}` : ''
                }`"
              >
                {{ col.prefix && col.prefix !== "" ? col.prefix : ""
                }}{{ formatValue(col.field, data[col.field]) }}
              </span>
            </slot>
          </div>
        </template>
      </Column>

      <template v-if="showExtensions">
        <Column
          v-for="col of extensionsColumns"
          :key="col.field"
          :field="col.field"
          :header="col.header"
          :sortable="col.sortable"
          :style="col.style"
          :headerClass="headerClass"
        >
          <template #body="{ data }">
            <slot :name="col.field" :data="data">
              <span class="text-[10px]">
                {{
                  typeof data[col.field] === "object" &&
                  data[col.field] !== null &&
                  "description" in data[col.field]
                    ? data[col.field].description
                    : data[col.field]
                }}
              </span>
            </slot>
          </template>
        </Column>
      </template>

      <template #paginatorcontainer>
        <div
          v-if="requiredArrow"
          class="w-full flex items-center justify-between"
        >
          <div class="flex items-center">
            <span class="mr-2 text-surface-primary dark:text-text-primary-dark"
              >Items por página: {{ rows }}</span
            >
            <div class="w-px h-11.25 bg-border-default"></div>
          </div>
          <div
            class="flex items-center cursor-pointer text-surface-primary dark:text-text-primary-dark"
          >
            Página {{ currentPage + 1 }} de {{ Math.ceil(totalRecords / rows) }}
            <div
              class="text-surface-primary dark:text-text-primary-dark cursor-pointer h-11.25 border-l border-border-default flex items-center justify-center ml-4"
              :class="{ 'opacity-50 pointer-events-none': currentPage === 0 }"
              @click="currentPage !== 0 && onPage(currentPage - 1)"
            >
              <font-awesome-icon icon="fa-solid fa-caret-left" class="mx-4" />
            </div>
            <div
              class="cursor-pointer h-11.25 border-l border-border-default flex items-center justify-center"
              :class="{
                'opacity-50 pointer-events-none':
                  currentPage + 1 == Math.ceil(totalRecords / rows),
              }"
              @click="
                currentPage + 1 != Math.ceil(totalRecords / rows) &&
                onPage(currentPage + 1)
              "
            >
              <font-awesome-icon
                icon="fa-solid fa-caret-right"
                class="ml-4 mr-1"
              />
            </div>
          </div>
        </div>
        <div
          v-if="requiredExtraButton"
          class="w-full flex items-center justify-between"
        >
          <slot name="footer"></slot>
        </div>
      </template>
    </DataTable>
  </div>
</template>

<script setup>
import { ref, computed, watch, useSlots } from "vue";
import CustomInput from "./CustomInput.vue";

import { DataTable, Column, Skeleton } from "primevue";

const slots = useSlots();

// Alignment helper functions with explicit class strings for Tailwind
const getJustifyClass = (align) => {
  if (align === "center") return "justify-center";
  if (align === "right" || align === "end") return "justify-end";
  if (align === "left" || align === "start") return "justify-start";
  return "";
};

const getTextAlignClass = (align) => {
  if (align === "center") return "text-center";
  if (align === "right" || align === "end") return "text-right";
  if (align === "left" || align === "start") return "text-left";
  return "";
};

// Initialize first page index
const first = ref(0);

// Component props definition
const props = defineProps({
  items: { type: Array, required: true },
  columns: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  requiredArrow: { type: Boolean, default: true },
  showCheckbox: { type: Boolean, default: false },
  showEditableRows: { type: Boolean, default: false },
  editableRowsCount: { type: Number, default: 0 },
  rows: { type: Number, default: 10 },
  tableClass: { type: String, default: "" },
  tableStyle: { type: String, default: "min-width: 50rem" },
  paginator: { type: Boolean, default: true },
  scrollable: { type: Boolean, default: false },
  selectedRows: { type: Array, required: false, default: [] },
  totalRecords: { type: Number, default: 0 },
  currentPage: { type: Number, default: 0 },
  lazy: { type: Boolean, default: true },
  alternateRows: { type: Boolean, default: false },
  requiredExtraButton: { type: Boolean, default: false },
  showExtensions: { type: Boolean, default: false },
  extensionsColumns: { type: Array, default: () => [] },
  rowClass: { type: String, default: "text-[15px]" },
  headerClass: {
    type: String,
    default:
      "text-[13px] text-text-title dark:text-text-primary-dark font-400 !border-b-[1px] !border-border-default",
  },
  expander: { type: Boolean, default: false },
  newRow: { type: Object, default: () => {} },
});

const emit = defineEmits([
  "onPageChange",
  "onRowsSelected",
  "onEditableRowsChange",
  "onAssignRow",
]);

// Filas editables
const editableRows = ref([]);

// Definir qué campos son editables
const editableFields = ["rut", "name", "sec", "precioCliente", "ph"];

// Crear filas editables iniciales
const createEmptyEditableRow = (useNewRowData = false) => {
  const row = {
    isEditable: true,
    id: `editable_${Date.now()}_${Math.random()}`,
  };

  props.columns.forEach((col) => {
    if (col.field !== "status") {
      // Si es la primera fila y tenemos datos en newRow, usar esos datos
      if (useNewRowData && props.newRow && props.newRow[col.field]) {
        row[col.field] = props.newRow[col.field];
      } else {
        // Para campos no editables, usar valores por defecto o vacíos
        if (editableFields.includes(col.field)) {
          row[col.field] = "";
        } else {
          // Campos no editables mantienen valores por defecto
          row[col.field] =
            col.field === "amountUSD"
              ? "0,00"
              : col.field === "precioCosto"
                ? "0,00"
                : col.field === "amountCLP"
                  ? "0"
                  : col.field === "utilidad"
                    ? "0"
                    : col.field === "condPago"
                      ? "-"
                      : "";
        }
      }
    }
  });

  return row;
};

// Función para verificar si un campo es editable
const isFieldEditable = (fieldName) => {
  return editableFields.includes(fieldName);
};

// Inicializar filas editables
watch(
  () => [props.editableRowsCount, props.newRow],
  ([newCount, newRowData]) => {
    editableRows.value = [];
    for (let i = 0; i < newCount; i++) {
      // La primera fila (i === 0) debe usar los datos de newRow si están disponibles
      const useNewRowData =
        i === 0 && newRowData && Object.keys(newRowData).length > 0;
      editableRows.value.push(createEmptyEditableRow(useNewRowData));
    }
  },
  { immediate: true, deep: true },
);

const allItems = computed(() => {
  if (props.showEditableRows) {
    return [...editableRows.value, ...props.items];
  }
  return props.items;
});

const addEditableRow = () => {
  // Las filas adicionales siempre son vacías
  editableRows.value.push(createEmptyEditableRow(false));
  emit("onEditableRowsChange", editableRows.value);
};

// Eliminar fila editable
const removeEditableRow = (index) => {
  const editableIndex = index;
  editableRows.value.splice(editableIndex, 1);
  emit("onEditableRowsChange", editableRows.value);
};

// Asignar fila
const assignRow = (data) => {
  emit("onAssignRow", data);
};

// Selected rows state
const selectRows = ref(props.selectedRows);

// Watch for changes in selected rows and emit event
watch(
  selectRows,
  (newSelection) => {
    emit("onRowsSelected", newSelection || []);
  },
  { deep: true },
);

// Watch for currentPage changes to update first
watch(
  () => props.currentPage,
  (newPage) => {
    first.value = newPage * props.rows;
  },
  { immediate: true },
);

function onPage(page) {
  emit("onPageChange", page);
}

// Format cell values
function formatValue(field, value) {
  if (typeof value === "object" && value !== null && "description" in value) {
    return value.description;
  }
  return value;
}
</script>

<style>
@reference "../../assets/base.css";
.p-datatable .p-datatable-thead > tr > th {
  @apply bg-surface-tertiary dark:bg-background-alt font-semibold 
    dark:text-white! text-gray-700  text-left p-3;
}
.p-datatable .p-datatable-tbody > tr > td {
  @apply border-b! text-gray-800 p-2 text-left border-border-default! 
    dark:text-white! dark:bg-surface-primary;
}
.p-datatable-column-title {
  @apply font-bold;
}
.p-datatable-thead {
  @apply !h-14 dark:!border-[1px] !border-border-default;
}
.p-paginator {
  @apply bg-surface-tertiary! py-0! px-3! rounded-b-lg flex items-center 
      dark:bg-background-alt! justify-between dark:text-white!;
}
.p-datatable-paginator-bottom {
  border: 0px solid #aab9c2 !important;
  border-top: 0px solid #aab9c2 !important;
  border-left: 0px solid #aab9c2 !important;
  border-right: 0px solid #aab9c2 !important;
}
.p-datatable-striped .p-datatable-tbody > tr:nth-child(even) > td {
  background-color: #f8f9fa !important;
}
.dark .p-datatable-striped .p-datatable-tbody > tr:nth-child(even) > td {
  background-color: #374151 !important;
}
.dark .p-datatable-striped .p-datatable-tbody > tr:nth-child(odd) > td {
  background-color: #1f2937 !important;
}
.p-datatable .p-datatable-tbody > tr:hover > td {
  @apply bg-gray-200 dark:bg-gray-600;
}
</style>
